import { useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import clsx from 'clsx'

import { type InsuranceOrderCardProps } from 'entities/common/my-ticket'
import {
  mapInsuredPersons,
  useInsuranceDetailsDialogs,
} from 'entities/insurance'

import { BuyTicketApi } from 'shared/api/BuyTicket'
import { CreatedInsurancePolicy, InsuranceApi } from 'shared/api/insurance'
import { CurrencySom } from 'shared/constants'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import { dateFormat } from 'shared/lib/dateFormat'
import { startPaymentFromConfirmResult } from 'shared/lib/payment-start'
import { priceShow } from 'shared/lib/priceShow'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { EmptyPage } from 'shared/ui/empty-page'
import { Loader } from 'shared/ui/loader'
import { message } from 'shared/ui/message'
import { Paper } from 'shared/ui/paper'

import { useInsuranceDraft } from '../../model/insurance-draft'
import { mapInsuranceDraftToPolicyRequest } from '../../model/policy-request'
import { InsuranceLayout } from '../../ui/insurance-layout'

import { CalculationFooter } from './ui/calculation-footer'

import s from './styles.module.scss'

const CalculationInsurancePage = () => {
  const { i18n, t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { draft } = useInsuranceDraft()
  const { showProgramDetails, showParticipants } = useInsuranceDetailsDialogs()
  const existingOrder = (
    location.state as { insuranceOrder?: InsuranceOrderCardProps } | null
  )?.insuranceOrder
  const isExistingOrder = Boolean(existingOrder)
  const [isPaying, setIsPaying] = useState(false)
  const [policy, setPolicy] = useState<CreatedInsurancePolicy | null>(null)
  const [isLoading, setIsLoading] = useState(!isExistingOrder)
  const [isError, setIsError] = useState(false)
  const [retryKey, setRetryKey] = useState(0)
  const policyRequestRef = useRef<ReturnType<
    typeof InsuranceApi.createPolicy
  > | null>(null)
  const payload = useMemo(
    () => (isExistingOrder ? null : mapInsuranceDraftToPolicyRequest(draft)),
    [draft, isExistingOrder],
  )

  useEffect(() => {
    if (isExistingOrder) {
      setIsLoading(false)
      setIsError(false)
      return
    }

    if (!payload) {
      setIsLoading(false)
      setIsError(true)
      return
    }

    const request =
      policyRequestRef.current || InsuranceApi.createPolicy(payload)
    let isActive = true

    policyRequestRef.current = request
    setIsLoading(true)
    setIsError(false)

    request
      .then(({ data }) => {
        if (!isActive) return

        if (!data.data.policy_id) {
          setIsError(true)
          return
        }

        setPolicy(data.data)
      })
      .catch(() => {
        if (isActive) {
          setIsError(true)
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false)
        }
      })

    return () => {
      isActive = false
    }
  }, [isExistingOrder, payload, retryKey])

  const formattedPrice =
    existingOrder?.price ||
    (policy ? `${priceShow(policy.conv_premiya_insurance)} ${CurrencySom}` : '')
  const participantCount =
    existingOrder?.participantsCount ?? draft.participants.length + 1
  const programTitle = existingOrder?.coverage || draft.program?.name || '—'
  const programDescription = existingOrder
    ? existingOrder.description
    : draft.program?.description || ''
  const insuredPersons = existingOrder
    ? existingOrder.participants || []
    : mapInsuredPersons(policy?.participants)
  const period = existingOrder
    ? t('insurance.vzr.calculation.period_value', {
        startDate: existingOrder.periodStart,
        endDate: existingOrder.periodEnd,
      })
    : draft.startDate && draft.endDate
      ? t('insurance.vzr.calculation.period_value', {
          startDate: dateFormat(
            draft.startDate,
            DATE_FORMAT_LIST.FULL_DATE_SHORT_YEAR,
          ),
          endDate: dateFormat(
            draft.endDate,
            DATE_FORMAT_LIST.FULL_DATE_SHORT_YEAR,
          ),
        })
      : '—'

  const summaryItems = [
    {
      icon: AllIcons.FLAG,
      label: t('insurance.vzr.calculation.country'),
      value:
        existingOrder?.title ||
        (i18n.language === 'en'
          ? draft.country?.nameEn
          : draft.country?.nameRu) ||
        '—',
    },
    {
      icon: AllIcons.EARTH,
      label: t('insurance.vzr.calculation.zone'),
      value: existingOrder?.zone || policy?.zone_name || '—',
    },
    {
      icon: AllIcons.CALENDAR_NEW,
      label: t('insurance.vzr.calculation.period'),
      value: period,
    },
    {
      icon: AllIcons.FILE,
      label: t('insurance.vzr.calculation.program'),
      value: programTitle,
      onShowDetails: programDescription
        ? () => showProgramDetails(programTitle, programDescription)
        : undefined,
    },
    {
      icon: AllIcons.INSURANCE_CALCULATION_PARTICIPANTS,
      label: t('insurance.vzr.calculation.participants'),
      value: String(participantCount),
      onShowDetails: insuredPersons.length
        ? () => showParticipants(insuredPersons)
        : undefined,
    },
    {
      icon: AllIcons.INSURANCE_CALCULATION_MONEY,
      label: t('insurance.vzr.calculation.price'),
      value: formattedPrice,
    },
  ]

  const onPay = async () => {
    const orderId = existingOrder?.id || policy?.policy_id

    if (!orderId) {
      message.error(t('error_des'))
      return
    }

    setIsPaying(true)

    try {
      const { data } = await BuyTicketApi.buyTicketConfirm({
        id: orderId,
        type: 'insurance',
      })
      const isPaymentStarted = await startPaymentFromConfirmResult(data)

      if (!isPaymentStarted) {
        message.error(t('payment_init_error'))
      }
    } catch {
      message.error(t('error_des'))
    } finally {
      setIsPaying(false)
    }
  }

  const isShowFooter =
    !isLoading && !isError && Boolean(existingOrder || policy)

  return (
    <InsuranceLayout
      title={t('insurance.vzr.calculation.title')}
      onGoBack={() => navigate(-1)}
      footer={
        isShowFooter
          ? {
              disabled: isPaying,
              loading: isPaying,
              borderRadius: 'md',
              className: clsx(s.payButton, 'semi-bold-17'),
              onClick: onPay,
              children: t('insurance.vzr.calculation.pay'),
              slot: <CalculationFooter price={formattedPrice} />,
            }
          : undefined
      }
    >
      {isLoading ? (
        <Loader marginTop={150} />
      ) : isError || (!existingOrder && !policy) ? (
        <div className="container">
          <EmptyPage
            title={t('insurance.vzr.errors.load_title')}
            subTitle={t('error_des')}
            buttonText={t('try_again')}
            onClick={() => {
              policyRequestRef.current = null
              setIsLoading(true)
              setIsError(false)
              setRetryKey((current) => current + 1)
            }}
          />
        </div>
      ) : (
        <Paper
          className={s.summary}
          padding={[0]}
          radius="16px"
        >
          {summaryItems.map((item, index) => (
            <div
              key={item.label}
              className={s.summaryRow}
            >
              <div className={s.iconWrapper}>
                <AppIcon
                  id={item.icon}
                  width={24}
                  height={24}
                  className={s.icon}
                />
              </div>
              <div className={s.summaryContent}>
                <div className={s.summaryText}>
                  <span className={clsx(s.summaryLabel, 'semi-bold-14')}>
                    {item.label}
                  </span>
                  <span className={clsx(s.summaryValue, 'medium-13')}>
                    {item.value}
                  </span>
                </div>
                {item.onShowDetails ? (
                  <button
                    type="button"
                    className={clsx(s.details, 'medium-12')}
                    onClick={item.onShowDetails}
                  >
                    {t('insurance.vzr.program.details')}
                  </button>
                ) : null}
                {index < summaryItems.length - 1 ? (
                  <div className={s.divider} />
                ) : null}
              </div>
            </div>
          ))}
        </Paper>
      )}
    </InsuranceLayout>
  )
}

export default CalculationInsurancePage
